import { alpha, createTheme } from '@mui/material';

const breakpoints = {
    keys: ['xs', 'sm', 'md', 'lg', 'xl'],
    values: { xs: 0, sm: 600, md: 900, lg: 1440, xl: 1920 },
};

const mixins = {
    toolbar: {
        minHeight: 56,
        '@media (min-width:0px) and (orientation: landscape)': { minHeight: 48 },
        '@media (min-width:600px)': { minHeight: 100 },
    },
};

const primaryScale = {
    50: '#F4F7F6',
    100: '#EEF3F2',
    200: '#E4ECEA',
    300: '#D4E0DD',
    400: '#B9CDC9',
    500: '#8FAFA8',
    600: '#679188',
    700: '#52736C',
    800: '#3F5953',
    900: '#2E413D',
    op50: 'rgba(46, 65, 61, 0.5)',
    op40: 'rgba(46, 65, 61, 0.4)',
    op30: 'rgba(46, 65, 61, 0.3)',
    op20: 'rgba(46, 65, 61, 0.2)',
    op10: 'rgba(46, 65, 61, 0.1)',
    op5: 'rgba(46, 65, 61, 0.05)',
};
const secondaryScale = {
    50: '#FBF6F0',
    100: '#F9F1E7',
    200: '#F6E8D8',
    300: '#F0DAC0',
    400: '#E7C39A',
    500: '#D89C58',
    600: '#BD7A2D',
    700: '#966123',
    800: '#744B1B',
    900: '#553714',
    op50: 'rgba(85, 55, 20, 0.50)',
    op40: 'rgba(85, 55, 20, 0.4)',
    op30: 'rgba(85, 55, 20, 0.3)',
    op20: 'rgba(85, 55, 20, 0.2)',
    op10: 'rgba(85, 55, 20, 0.1)',
    op5: 'rgba(85, 55, 20, 0.05)',
};
const darkScale = {
    50: '#F5F7F8',
    100: '#EFF2F3',
    200: '#E6EBED',
    300: '#D7DEE2',
    400: '#BFCBD1',
    500: '#98ABB4',
    600: '#748D9A',
    700: '#596F7A',
    800: '#45565F',
    900: '#323F45',
    op50: 'rgba(50, 63, 69, 0.5)',
    op40: 'rgba(50, 63, 69, 0.4)',
    op30: 'rgba(50, 63, 69, 0.3)',
    op20: 'rgba(50, 63, 69, 0.2)',
    op10: 'rgba(50, 63, 69, 0.1)',
    op5: 'rgba(50, 63, 69, 0.05)',
};
const palette = {
    primaryScale,
    secondaryScale,
    darkScale,
    /* -------------------------------------------------------------------------- */
    common: { black: '#000', white: '#fff' },
    primary: {
        light: primaryScale[700],
        main: primaryScale[800],
        dark: primaryScale[900],
        contrastText: darkScale[50],
    },
    secondary: {
        light: secondaryScale[500],
        main: secondaryScale[600],
        dark: secondaryScale[700],
        contrastText: '#fff',
    },
    neutral: darkScale[600],
    error: {
        light: alpha('#F44336', 0.1),
        main: '#F10D0D',
        dark: '#F44336',
        contrastText: '#fff',
    },
    /** @deprecated */
    warning: {
        light: alpha('#EC9235', 0.1),
        main: '#EC9235',
        dark: '#EC9235',
        contrastText: '#fff',
    },
    /** @deprecated */
    info: {
        light: alpha('#E7EDF9', 0.75),
        main: '#E7EDF9',
        dark: '#BAC4DB',
        contrastText: '#426771',
    },
    success: {
        light: '#E5F6EE',
        main: '#42BD7F',
        dark: '#18452F',
        contrastText: '#000',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
    text: {
        primary: darkScale[900],
        secondary: secondaryScale[900],
        disabled: darkScale[700],
    },
    divider: darkScale[200],
    background: { paper: '#fff', default: '#ffff' },
    action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hover: 'rgba(0, 0, 0, 0.04)',
        hoverOpacity: 0.04,
        selected: 'rgba(0, 0, 0, 0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        disabledOpacity: 0.38,
        focus: 'rgba(0, 0, 0, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
    },
    deprecated_secondaryWhiteCloud: {
        main: '#f8f8f8',
    },
    deprecated_secondarySunnyDay: {
        main: '#f6f3E5',
        dark: '#BDBDBD',
    },
    deprecated_secondaryMorningMist: {
        main: '#e7edf9',
    },
    deprecated_secondaryDrySage: {
        main: '#8e8b76',
    },
    deprecated_errorLight: {
        light: '#ff6c6c',
        main: '#f44336',
        dark: '#d32f2f',
        contrastText: '#fff',
    },
    /* ------------------------------- customColors ---------------------------- */
    deprecated_spanishWhite: {
        light: '#F9F5E9',
        main: '#D4C8B7',
        dark: '#D4CDB7',
        contrastText: '#426771',
    },
    deprecated_spanishWhiteBgShadow: '#f5ead5',
    deprecated_spanishWhiteShadow: '#ecdec5',
    deprecated_shamrock: {
        light: '#2cd06b',
        main: '#06C74E',
        dark: '#36B37F',
        contrastText: '#ffffff',
    },
    deprecated_driftwood: {
        light: '#b07c48',
        main: '#8F5B28',
        dark: '#64370B',
        contrastText: '#ffffff',
    },
    deprecated_linkWater: {
        light: '#f5f7fa',
        main: '#C2CFE9',
        dark: '#052748',
        contrastText: '#426771',
    },
};

const shadowKeyUmbraOpacity = 0.2;
const shadowKeyPenumbraOpacity = 0.14;
const shadowAmbientShadowOpacity = 0.12;
const shadowMenu = 'rgba(50,63,69,0.08)';
const createShadow = (px, shadowColor) => {
    return [
        `${px[0]}px ${px[1]}px ${px[2]}px ${px[3]}px ${shadowColor !== null && shadowColor !== void 0 ? shadowColor : 'rgba(0,0,0,' + shadowKeyUmbraOpacity + ')'}`,
        `${px[4]}px ${px[5]}px ${px[6]}px ${px[7]}px ${shadowColor !== null && shadowColor !== void 0 ? shadowColor : 'rgba(0,0,0,' + shadowKeyPenumbraOpacity + ')'}`,
        `${px[8]}px ${px[9]}px ${px[10]}px ${px[11]}px ${shadowColor !== null && shadowColor !== void 0 ? shadowColor : 'rgba(0,0,0,' + shadowAmbientShadowOpacity + ')'}`,
    ].join(',');
};
// https://github.com/mui/material-ui/blob/v5.14.5/packages/mui-material/src/styles/shadows.js
// Values from https://github.com/material-components/material-components-web/blob/be8747f94574669cb5e7add1a7c54fa41a89cec7/packages/mdc-elevation/_variables.scss
const shadows = [
    'none',
    createShadow([0, 2, 1, -1, 0, 1, 1, 0, 0, 1, 3, 0]),
    createShadow([0, 3, 1, -2, 0, 2, 2, 0, 0, 1, 5, 0]),
    createShadow([0, 3, 3, -2, 0, 3, 4, 0, 0, 1, 8, 0]),
    createShadow([0, 2, 4, -1, 0, 4, 5, 0, 0, 1, 10, 0]),
    createShadow([0, 3, 5, -1, 0, 5, 8, 0, 0, 1, 14, 0]),
    createShadow([0, 3, 5, -1, 0, 6, 10, 0, 0, 1, 18, 0]),
    createShadow([0, 4, 5, -2, 0, 7, 10, 1, 0, 2, 16, 1]),
    createShadow([0, 8, 64, 0, 0, 2, 16, -4, 0, 8, 32, -2], shadowMenu), // MuiPopover-paper-MuiMenu-paper-menu
    createShadow([0, 5, 6, -3, 0, 9, 12, 1, 0, 3, 16, 2]),
    createShadow([0, 6, 6, -3, 0, 10, 14, 1, 0, 4, 18, 3]),
    createShadow([0, 6, 7, -4, 0, 11, 15, 1, 0, 4, 20, 3]),
    createShadow([0, 7, 8, -4, 0, 12, 17, 2, 0, 5, 22, 4]),
    createShadow([0, 7, 8, -4, 0, 13, 19, 2, 0, 5, 24, 4]),
    createShadow([0, 7, 9, -4, 0, 14, 21, 2, 0, 5, 26, 4]),
    createShadow([0, 8, 9, -5, 0, 15, 22, 2, 0, 6, 28, 5]),
    createShadow([0, 8, 10, -5, 0, 16, 24, 2, 0, 6, 30, 5]),
    createShadow([0, 8, 11, -5, 0, 17, 26, 2, 0, 6, 32, 5]),
    createShadow([0, 9, 11, -5, 0, 18, 28, 2, 0, 7, 34, 6]),
    createShadow([0, 9, 12, -6, 0, 19, 29, 2, 0, 7, 36, 6]),
    createShadow([0, 10, 13, -6, 0, 20, 31, 3, 0, 8, 38, 7]),
    createShadow([0, 10, 13, -6, 0, 21, 33, 3, 0, 8, 40, 7]),
    createShadow([0, 10, 14, -6, 0, 22, 35, 3, 0, 8, 42, 7]),
    createShadow([0, 11, 14, -7, 0, 23, 36, 3, 0, 9, 44, 8]),
    createShadow([0, 11, 15, -7, 0, 24, 38, 3, 0, 9, 46, 8]),
];

const typography = {
    htmlFontSize: 16,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontFamily: '"BrownStd-Bold","BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
    extradisplay: {
        fontSize: '4.2rem',
        lineHeight: '120%' /* 80.4px */,
        letterSpacing: '-4px',
        fontWeight: 'bold',
    },
    display: {
        fontSize: '3.6rem',
        lineHeight: '120%' /* 60px */,
        letterSpacing: '-2px',
        fontWeight: 'bold',
    },
    h1: {
        fontSize: '2.4rem',
        lineHeight: '120%' /* 44.4px */,
        letterSpacing: '-1px',
        fontWeight: 'bold',
    },
    h2: {
        fontSize: '1.8rem',
        lineHeight: '140%' /* 39.2px */,
        letterSpacing: '-0.5px',
        fontWeight: 'bold',
    },
    h3: {
        fontSize: '1.3rem',
        lineHeight: '140%' /* 29.4px */,
        letterSpacing: '-0.5px',
        fontWeight: 'bold',
    },
    body: {
        fontSize: '1rem',
        lineHeight: '140%' /* 22.4px */,
        fontWeight: 'bold',
    },
    body2: {
        fontSize: '1rem',
        lineHeight: '140%' /* 22.4px */,
        fontWeight: 400,
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
    },
    caps: {
        fontSize: '14px',
        lineHeight: '140%' /* 19.6px */,
        letterSpacing: '1px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
    },
    caption: {
        fontSize: '14px',
        lineHeight: '140%' /* 19.6px */,
        fontWeight: 'bold',
    },
    subcaption: {
        fontSize: '0.75rem',
        lineHeight: '140%' /* 16.8px */,
        fontWeight: 'bold',
    },
    button1: {
        fontSize: '1rem',
        lineHeight: '140%' /* 19.6px */,
        fontWeight: 'bold',
    },
    button2: {
        fontSize: '0.875rem',
        lineHeight: '140%' /* 22.4px */,
        fontWeight: 'bold',
    },
    button3: {
        fontSize: '0.75rem',
        lineHeight: '140%' /* 16.8px */,
        fontWeight: 'bold',
    },
    label1: {
        fontSize: '1rem',
        lineHeight: '150%' /* 24px */,
    },
    deprecated_h1: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 300,
        fontSize: '6rem',
        lineHeight: 1.167,
        letterSpacing: '-0.01562em',
        color: '#2D525C',
    },
    deprecated_h2: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 300,
        fontSize: '3.75rem',
        lineHeight: 1.2,
        letterSpacing: '-0.00833em',
        color: '#2D525C',
    },
    deprecated_h3: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '3rem',
        lineHeight: 1.167,
        letterSpacing: '0em',
        color: '#2D525C',
    },
    deprecated_h4: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.30rem',
        lineHeight: 1.3,
        letterSpacing: '0em',
        color: '#2D525C',
    },
    deprecated_h5: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.95rem',
        lineHeight: 1.4,
        letterSpacing: '0em',
        color: '#2D525C',
    },
    deprecated_h6: {
        fontFamily: '"BrownStd-Bold","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 100,
        fontSize: '0.8rem',
        lineHeight: 1.2,
        letterSpacing: '0.01em',
        color: '#2D525C',
    },
    deprecated_subtitle1: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: 1.75,
        letterSpacing: '0.00938em',
        color: '#2D525C',
    },
    deprecated_subtitle2: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1.57,
        letterSpacing: '0.00714em',
        color: '#2D525C',
    },
    deprecated_body1: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 200,
        fontSize: '0.8rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
        color: '#2D525C',
    },
    deprecated_body2: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: 1.43,
        letterSpacing: '0.01071em',
        color: '#2D525C',
    },
    deprecated_caption: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 100,
        fontSize: '0.8rem',
        lineHeight: 1.2,
        letterSpacing: '0.01em',
        color: '#2D525C',
    },
    deprecated_button: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 200,
        fontSize: '0.8rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
        color: '#2D525C',
    },
    deprecated_inherit: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 200,
        fontSize: '0.8rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
        color: '#2D525C',
    },
    deprecated_overline: {
        fontFamily: '"BrownStd-Regular","Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 200,
        fontSize: '0.8rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em',
        color: '#2D525C',
    },
};

const shape = {
    borderRadiusS: 8,
    borderRadius: 16,
    borderRadiusM: 16,
    borderRadiusL: 24,
};

const transitions = {
    easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
    },
};

const zIndex = {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
};

const appBarCss = {
    root: {
        backgroundColor: 'transparent !important',
    },
};
const MuiAppBar = { styleOverrides: appBarCss };

const autocompleteCss = {
    input: {
        padding: '0 !important',
    },
    listbox: ({ theme }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        maxHeight: '220px',
        padding: 0,
        margin: theme.spacing(1, 0),
    }),
};
const MuiAutocomplete = { styleOverrides: autocompleteCss };

const avatarCss = {
    root: {
        boxShadow: '0 0 10px 0px #DDD',
        border: '3px solid #FFF',
    },
};
const MuiAvatar = { styleOverrides: avatarCss };

const buttonCss = {
    root: {
        textTransform: 'none',
        borderRadius: '10px',
    },
    contained: {
        textTransform: 'none',
        padding: '16px 30px 16px',
        lineHeight: 1,
        '&:hover': {},
    },
    text: ({ theme }) => ({
        border: '1px solid',
        borderColor: 'transparent',
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            color: theme.palette.darkScale[600],
        },
        ':hover': {
            background: theme.palette.primaryScale[100],
        },
        ':focus': Object.assign(Object.assign({}, theme.customShadows.focus), { backgroundColor: theme.palette.darkScale[50], borderColor: theme.palette.primaryScale[600] }),
    }),
    containedPrimary: ({ theme }) => ({
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            background: theme.palette.darkScale[300],
            color: theme.palette.darkScale[600],
        },
        background: theme.palette.primary.main,
        ':hover': {
            background: theme.palette.primary.light,
        },
        ':active': {
            background: theme.palette.primary.dark,
        },
    }),
    containedSecondary: ({ theme }) => ({
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            background: theme.palette.darkScale[300],
            color: theme.palette.darkScale[600],
        },
        background: theme.palette.secondary.main,
        ':hover': {
            background: theme.palette.secondary.light,
        },
        ':active': {
            background: theme.palette.secondary.dark,
        },
    }),
    sizeLarge: ({ theme }) => (Object.assign({ padding: '16px 20px', gap: '12px' }, theme.typography.button1)),
    iconSizeLarge: {
        '>svg': {
            fontSize: '24px !important',
        },
        '&.MuiButton-startIcon': {
            margin: 0,
        },
        '&.MuiButton-endIcon': {
            margin: 0,
        },
    },
    sizeMedium: ({ theme }) => (Object.assign({ padding: '12px 16px', gap: '8px' }, theme.typography.button2)),
    iconSizeMedium: {
        '>svg': {
            fontSize: '20px !important',
        },
        '&.MuiButton-startIcon': {
            margin: 0,
        },
        '&.MuiButton-endIcon': {
            margin: 0,
        },
    },
    sizeSmall: ({ theme }) => (Object.assign({ padding: '8px', gap: '8px' }, theme.typography.button3)),
    iconSizeSmall: {
        '>svg': {
            fontSize: '16px !important',
        },
        '&.MuiButton-startIcon': {
            margin: 0,
        },
        '&.MuiButton-endIcon': {
            margin: 0,
        },
    },
    containedSuccess: ({ theme }) => ({
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            background: theme.palette.darkScale[300],
            color: theme.palette.darkScale[600],
        },
        background: theme.palette.success.main,
        ':hover': {
            background: theme.palette.success.light,
        },
        ':active': {
            background: theme.palette.success.dark,
        },
    }),
};
const buttonProperties = {
    variant: 'contained',
    disableElevation: true,
    disableRipple: true,
};
const MuiButton = { styleOverrides: buttonCss, defaultProps: buttonProperties };

const CheckboxCss = {
    root: ({ theme }) => ({
        borderRadius: '4px',
        border: `1px solid ${theme.palette.darkScale[400]}`,
        backgroundColor: theme.palette.common.white,
        '&.MuiCheckbox-sizeSmall': {
            width: '20px',
            height: '20px',
        },
        '&.MuiCheckbox-sizeMedium': {
            width: '24px',
            height: '24px',
        },
        '&.Mui-checked': {},
        '& svg': {
            fill: theme.palette.success.main,
        },
        '&.Mui-disabled': {
            borderColor: theme.palette.darkScale[600],
            backgroundColor: theme.palette.darkScale[600],
            svg: {
                fill: theme.palette.common.white,
            },
        },
    }),
};
const MuiCheckbox = { styleOverrides: CheckboxCss };

const RadioButtonCss = {
    root: ({ theme }) => ({
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        border: `2px solid ${theme.palette.darkScale[400]}`,
        '.MuiFormControlLabel-root &': {
            marginRight: '8px',
        },
        '&.Mui-checked ': {
            backgroundColor: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            svg: {
                fill: theme.palette.common.white,
            },
            '&.Mui-disabled': {
                backgroundColor: theme.palette.darkScale[600],
                borderColor: theme.palette.darkScale[600],
            },
        },
    }),
};
const MuiRadio = { styleOverrides: RadioButtonCss };

const FormControlCss = {
    root: ({ theme }) => ({
        marginLeft: 0,
        marginRight: 0,
        '& .MuiTypography-body1': Object.assign(Object.assign({}, theme.typography.h6), { fontSize: '0.95rem', color: theme.palette.primary.light, marginLeft: '2px', transform: 'translate(0, 2px)' }),
    }),
};
const MuiFormControl = { styleOverrides: FormControlCss };

const FormControlLabelCss = {
    root: ({ theme }) => ({
        '&.label--radio-button': {
            margin: 0,
            padding: '16px',
            borderRadius: '8px',
            '&:hover': {
                backgroundColor: theme.palette.darkScale[50],
            },
            '&:has(.Mui-checked)': {
                backgroundColor: theme.palette.success.light,
                '&:hover': {
                    backgroundColor: theme.palette.darkScale[200],
                },
            },
            '&.Mui-disabled': {
                backgroundColor: 'transparent',
                pointerEvents: 'none',
            },
        },
    }),
    label: () => ({
        '&:not(.MuiTypography-label1)': {
            fontSize: '0.95rem',
        },
        '&.MuiTypography-label1': {
            marginLeft: '0.5rem',
        },
    }),
};
const MuiFormControlLabel = { styleOverrides: FormControlLabelCss };

const iconButtonCss = {
    root: ({ theme }) => ({
        textTransform: 'none',
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'transparent',
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            color: theme.palette.darkScale[600],
        },
        ':hover': {
            background: theme.palette.primaryScale[100],
        },
        ':focus': Object.assign(Object.assign({}, theme.customShadows.focus), { backgroundColor: theme.palette.darkScale[50], borderColor: theme.palette.primaryScale[600] }),
    }),
    sizeLarge: ({ theme }) => (Object.assign(Object.assign({ padding: '16px', gap: '12px' }, theme.typography.button1), { '>svg': {
            fontSize: '24px !important',
        } })),
    sizeMedium: ({ theme }) => (Object.assign(Object.assign({ padding: '12px', gap: '8px' }, theme.typography.button2), { '>svg': {
            fontSize: '20px !important',
        } })),
    sizeSmall: ({ theme }) => (Object.assign(Object.assign({ padding: '8px', gap: '8px' }, theme.typography.button3), { '>svg': {
            fontSize: '16px !important',
        } })),
};
const iconButtonProperties = {
    disableRipple: true,
};
const MuiIconButton = {
    styleOverrides: iconButtonCss,
    defaultProps: iconButtonProperties,
};

const InputBaseCss = {
    root: () => ({}),
    input: {},
};
const MuiInputBase = { styleOverrides: InputBaseCss };

const selectCss = {
    root: ({ theme }) => (Object.assign(Object.assign({}, theme.customShadows.input), { width: '100%', fieldset: {
            borderRadius: theme.spacing(1),
            border: `1px solid ${theme.palette.darkScale[300]}`,
        }, '&:hover': {
            fieldset: Object.assign(Object.assign({}, theme.customShadows.focus), { borderWidth: '2px', borderColor: `${theme.palette.primaryScale['500']} !important` }),
        }, '&.MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            backgroundColor: 'white',
        }, '&.MuiOutlinedInput-root.Mui-focused': {
            fieldset: Object.assign(Object.assign({}, theme.customShadows.focus), { borderWidth: '2px', borderColor: `${theme.palette.primaryScale['600']} !important` }),
        }, '&.Mui-error': {
            '&:hover, &.MuiOutlinedInput-root.Mui-focused': {
                fieldset: {
                    borderWidth: '2px',
                    borderColor: `${theme.palette.error.main} !important`,
                },
            },
        } })),
    select: ({ theme }) => (Object.assign(Object.assign({}, theme.typography.body1), { color: theme.palette.darkScale[700], textOverflow: 'unset', textWrap: 'wrap' })),
};
const MuiSelect = { styleOverrides: selectCss };

const sliderCss = {
    root: ({ theme }) => ({
        width: '90%',
        '&.Mui-disabled': {
            opacity: 0.6,
            color: theme.palette.primary.light,
            '& .MuiSlider-thumb': {
                width: '14px',
                height: '14px',
            },
        },
    }),
    rail: ({ theme }) => ({
        height: '10px',
        borderRadius: '10px',
        backgroundColor: 'white',
        opacity: 1,
        border: `1px solid ${theme.palette.secondary.main}`,
        boxSizing: 'border-box',
    }),
    valueLabel: ({ theme }) => ({
        left: '-13px',
        top: 0,
        background: 'transparent',
        '& *': {
            color: theme.palette.primary.main,
        },
    }),
    mark: { visibility: 'hidden' },
    markLabel: ({ theme }) => ({
        marginLeft: '5px',
        top: '-10px',
        color: theme.palette.secondary.main,
    }),
    marked: {
        marginBottom: 0,
    },
    track: ({ theme }) => ({
        height: '10px',
        borderRadius: '10px',
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
    }),
    thumb: {
        width: '14px',
        height: '14px',
    },
};
const MuiSlider = { styleOverrides: sliderCss };

const tabCss = {
    root: {
        backgroundColor: 'transparent',
    },
    textColorSecondary: ({ theme }) => ({
        color: theme.palette.secondaryScale[200],
        '&.Mui-selected': {
            color: theme.palette.secondaryScale[200],
        },
        '&:hover': {
            color: `${theme.palette.primary.main} !important`,
        },
        fontWeight: 'bold',
        transition: 'color .3s ease',
    }),
};
const MuiTab = { styleOverrides: tabCss };

const tabsCss = {
    root: {
        backgroundColor: 'transparent',
        borderRadius: '15px 15px 0 0',
    },
    indicator: ({ theme }) => ({
        backgroundColor: theme.palette.secondaryScale[600],
    }),
    flexContainer: ({ theme }) => ({
        boxSizing: 'border-box',
        borderBottom: `2px solid ${theme.palette.secondaryScale[800]}`,
    }),
};
const MuiTabs = { styleOverrides: tabsCss };

const textFieldCss = {
    root: ({ theme }) => ({
        '& .MuiInputBase-root': Object.assign(Object.assign({ backgroundColor: theme.palette.common.white }, theme.customShadows.input), { input: Object.assign(Object.assign({ borderRadius: 'inherit', height: '100%', padding: '0 16px', color: theme.palette.darkScale[900] }, theme.typography.body), { backgroundColor: 'transparent', '&::placeholder': {
                    opacity: 1,
                    color: theme.palette.darkScale[700],
                }, '&:autofill, &:-webkit-autofill': {
                    boxShadow: `0 0 0 50px ${theme.palette.success.light} inset`,
                } }), '&:has(input:autofill, input:-webkit-autofill)': {
                backgroundColor: theme.palette.success.light,
            }, height: '56px', fieldset: {
                top: '0',
                legend: {
                    display: 'none',
                },
            }, borderColor: theme.palette.darkScale[300], '&:hover,&.Mui-focused,&.Mui-error': Object.assign(Object.assign({}, theme.customShadows.focus), { fieldset: {
                    borderColor: theme.palette.darkScale[500],
                    borderWidth: '2px',
                } }), '&.Mui-focused': {
                fieldset: {
                    borderColor: theme.palette.primaryScale[600],
                },
            }, '&.Mui-error': {
                fieldset: {
                    borderColor: theme.palette.error.main,
                },
            }, borderRadius: '8px', '.MuiInputAdornment-positionEnd': Object.assign(Object.assign({}, theme.typography.caption), { color: theme.palette.darkScale[700], height: '24px', borderLeft: `1px solid ${theme.palette.darkScale[300]}`, paddingLeft: '16px' }) }),
        '& .MuiFormLabel-root': {
            color: theme.palette.darkScale[900] + '!important',
            position: 'relative',
            textTransform: 'uppercase',
            transform: 'none',
            fontSize: '14px',
            marginBottom: '8px',
        },
        '& .MuiFormHelperText-root': {
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '140%',
            color: theme.palette.darkScale[700] + '!important',
            margin: 0,
            marginTop: '8px',
        },
    }),
};
const MuiTextField = { styleOverrides: textFieldCss };

const tooltipCss$1 = {
    tooltip: ({ theme }) => (Object.assign(Object.assign({}, theme.customShadows.tooltip), { backgroundColor: theme.palette.common.white, color: theme.palette.darkScale[900], borderRadius: '8px', padding: '12px', '&.dark': {
            backgroundColor: theme.palette.darkScale[900],
            color: theme.palette.common.white,
        } })),
    arrow: ({ theme }) => ({
        color: theme.palette.common.white,
        '.dark &': {
            color: theme.palette.darkScale[900],
        },
    }),
};
const MuiTooltip = { styleOverrides: tooltipCss$1 };

const styleOverrides = {
    root: ({ theme }) => ({
        height: '100%',
        width: '100%',
        borderRadius: 5,
        backgroundColor: theme.palette.primaryScale[200],
    }),
    bar: {
        borderRadius: 5,
    },
};
const MuiLinearProgress = { styleOverrides };

const InputLabelCss = {
    root: ({ theme }) => (Object.assign({}, theme.typography.caption)),
};
const MuiInputLabel = { styleOverrides: InputLabelCss };

const styles = {
    scrollSection: {
        '&::-webkit-scrollbar': {
            width: '22px',
            height: '22px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: palette.darkScale[200],
            borderRadius: '99px',
            border: `8px solid white`, // the border-color should be overrided if the background-color componentn is not white.
        },
    },
    disabledInput: {
        opacity: '50%',
        pointerEvents: 'none',
        userSelect: 'none',
    },
};

const customShadows = {
    input: {
        boxShadow: `0px 4px 16px 4px ${palette.darkScale.op5}`,
    },
    focus: {
        boxShadow: `0px 0px 0px 2px ${palette.primaryScale[300]}`,
    },
    modal: {
        boxShadow: `0px 8px 16px 0px ${palette.darkScale.op10}`,
    },
    tooltip: {
        boxShadow: `0px 8px 32px 0px ${palette.darkScale.op10}, 0px 2px 8px -2px ${palette.darkScale.op10}`,
    },
};

const menuCss = {
    list: ({ theme }) => ({
        display: 'flex',
        padding: theme.spacing(1),
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingRight: `${theme.spacing(1)} !important`,
    }),
};
const MuiMenu = { styleOverrides: menuCss };

const menuItemCss = {
    root: ({ theme }) => (Object.assign(Object.assign({ whiteSpace: 'normal', padding: theme.spacing(0.5, 1) }, theme.typography.caption), { borderRadius: theme.shape.borderRadiusS, '&:hover, &:focus': {
            backgroundColor: theme.palette.darkScale[100],
        } })),
};
const MuiMenuItem = { styleOverrides: menuItemCss };

const ListItemButtonCss = {
    root: ({ theme }) => ({
        '&.MuiListItemIcon-root': {
            minWidth: 'auto',
            marginLeft: theme.spacing(0.5),
            svg: {
                color: theme.palette.darkScale[900],
            },
        },
        '&.MuiListItemText-root': {
            margin: 0,
        },
    }),
};
const MuiListItemButton = { styleOverrides: ListItemButtonCss };

const tooltipCss = {
    root: ({ theme }) => ({
        padding: '8px 0',
        borderRadius: '48px',
        backgroundColor: theme.palette.primaryScale[50],
        height: 'auto',
        '&.chip--text': {
            borderRadius: '8px',
        },
        '&.chip--action': {
            border: `1px solid ${theme.palette.primaryScale[200]}`,
            '&:hover': {
                backgroundColor: theme.palette.primaryScale[200],
            },
        },
        '.MuiChip-label': Object.assign({ padding: '0px 16px' }, theme.typography.body),
        '.MuiChip-deleteIcon': {
            margin: '0 10px 0 -10px',
            color: theme.palette.darkScale[900],
            fontSize: '1.25em',
            '&:hover': {
                color: theme.palette.darkScale[900],
            },
        },
    }),
};
const MuiChip = { styleOverrides: tooltipCss };

const typographyDefaultProps = {
    variant: 'body',
};
const MuiTypography = { defaultProps: typographyDefaultProps };

const themeOptions = {
    breakpoints,
    direction: 'ltr',
    mixins,
    components: {
        MuiAppBar,
        MuiAutocomplete,
        MuiAvatar,
        MuiButton,
        MuiCheckbox,
        MuiRadio,
        MuiFormControl,
        MuiFormControlLabel,
        MuiIconButton,
        MuiInputBase,
        MuiSelect,
        MuiSlider,
        MuiTab,
        MuiTabs,
        MuiTextField,
        MuiTooltip,
        MuiLinearProgress,
        MuiInputLabel,
        MuiMenu,
        MuiMenuItem,
        MuiListItemButton,
        MuiChip,
        MuiTypography,
    },
    palette,
    shadows,
    typography,
    shape,
    transitions,
    zIndex,
    spacing: 8,
    /* ----------------------------- Custom Options ----------------------------- */
    styles,
    customShadows,
};
const theme = createTheme(themeOptions);

export { theme as a, themeOptions as t };
//# sourceMappingURL=theme-f4aeefe2.js.map
